'use client';

import { c as _c } from "react/compiler-runtime";
import { SvgAdd } from '@common/icon';
import { cn } from '@core/utils';
import { useLinkStatus } from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { ServiceSwitchDropdown } from '@/app/_components/ServiceSwitchDropdown';
import { DONATION_BNB_TOAST_ITEMS } from '@/app/donation/constants';
import { DONATION_ROUTES, ROUTES, SUB_DOMAIN } from '@/constants';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { gtmDatasets } from '@/utils/analytics/gtm';
const ANIMATION_DURATION = 300;
type DonationBnbLinkContentProps = Pick<(typeof DONATION_BNB_TOAST_ITEMS)[number], 'label' | 'icon'> & {
  isActive: boolean;
};
const DonationBnbLinkContent = t0 => {
  const $ = _c(24);
  const {
    label,
    icon: Icon,
    isActive
  } = t0;
  const {
    pending
  } = useLinkStatus();
  const iconContainerRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== pending) {
    t1 = () => {
      if (!pending) {
        return;
      }
      const linkElement = iconContainerRef.current?.closest("a");
      if (!linkElement) {
        return;
      }
      const preventRepeatedNavigation = _temp;
      linkElement.setAttribute("aria-busy", "true");
      linkElement.setAttribute("aria-disabled", "true");
      linkElement.addEventListener("click", preventRepeatedNavigation, true);
      return () => {
        linkElement.removeAttribute("aria-busy");
        linkElement.removeAttribute("aria-disabled");
        linkElement.removeEventListener("click", preventRepeatedNavigation, true);
      };
    };
    t2 = [pending];
    $[0] = pending;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== pending) {
    t3 = cn("transition-opacity", {
      "animate-pulse opacity-60 motion-reduce:animate-none": pending
    });
    $[3] = pending;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = isActive || pending;
  let t5;
  if ($[5] !== t4) {
    t5 = cn("size-28 text-Gray-400", {
      "text-Gray-black": t4
    });
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== Icon || $[8] !== t5) {
    t6 = <Icon className={t5} />;
    $[7] = Icon;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t6) {
    t7 = <div ref={iconContainerRef} className={t3}>{t6}</div>;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = isActive || pending;
  let t9;
  if ($[13] !== t8) {
    t9 = cn("whitespace-nowrap text-center font-500 text-Gray-550 transition-colors typo-caption2", {
      "font-600 text-Gray-900": t8
    });
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== label || $[16] !== t9) {
    t10 = <p className={t9}>{label}</p>;
    $[15] = label;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = pending ? "\uD398\uC774\uC9C0 \uC774\uB3D9 \uC911" : null;
  let t12;
  if ($[18] !== t11) {
    t12 = <span aria-live="polite" className="sr-only" role="status">{t11}</span>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t10 || $[21] !== t12 || $[22] !== t7) {
    t13 = <>{t7}{t10}{t12}</>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t7;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  return t13;
};
export const DonationBnb = () => {
  const $ = _c(15);
  const pathname = usePathname();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [menuClickAnimated, setMenuClickAnimated] = useState(t0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const isToss = useDeviceInfoStore(_temp2);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      type: MESSAGE_KEYWORD.HAPTIC
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    request: requestHaptic
  } = useHandleApp(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = index => {
      setMenuClickAnimated(prev => ({
        ...prev,
        [index]: true
      }));
      setTimeout(() => {
        setMenuClickAnimated(prev_0 => ({
          ...prev_0,
          [index]: false
        }));
      }, ANIMATION_DURATION);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleMenuClick = t2;
  let t3;
  if ($[3] !== isDropdownOpen || $[4] !== isToss) {
    t3 = !isToss ? <div className="relative z-50"><button type="button" className="relative z-50 size-40 rounded-pill bg-Gray-200 flex-center" onClick={() => setIsDropdownOpen(_temp3)} aria-label={"\uB2E4\uB978 \uC11C\uBE44\uC2A4\uB85C \uC774\uB3D9"} aria-expanded={isDropdownOpen}><SvgAdd className={cn("size-20 text-Gray-600 transition-transform duration-200", {
          "rotate-45": isDropdownOpen
        })} /></button><ServiceSwitchDropdown isOpen={isDropdownOpen} onClose={() => setIsDropdownOpen(false)} currentService="donation" /></div> : null;
    $[3] = isDropdownOpen;
    $[4] = isToss;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== menuClickAnimated || $[7] !== pathname || $[8] !== requestHaptic) {
    t4 = DONATION_BNB_TOAST_ITEMS.map((t5, index_0) => {
      const {
        label,
        route,
        icon: Icon
      } = t5;
      const isActive = (() => {
        if (!pathname) {
          return false;
        }
        if (route.pathname === DONATION_ROUTES.HOME.pathname) {
          return pathname === route.pathname;
        }
        if (route.pathname === DONATION_ROUTES.MY.pathname) {
          return pathname === ROUTES.MY.pathname || pathname.includes(route.pathname);
        }
        return pathname.includes(route.pathname);
      })();
      const isCurrentPage = pathname === route.pathname;
      const clickFnbDataset = gtmDatasets.clickFnb({
        menu_name: label
      });
      return <li key={label} className="min-w-0 flex-1"><CustomLink href={route.pathname} aria-current={isCurrentPage ? "page" : undefined} aria-label={`${label} 페이지로 이동`} prefetch={true} className={cn("flex w-full flex-col items-center", menuClickAnimated[index_0] && "animate-jump")} style={{
          animationDuration: `${ANIMATION_DURATION}ms`
        }} onClick={event => {
          if (isCurrentPage) {
            event.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          requestHaptic();
          handleMenuClick(index_0);
        }} {...clickFnbDataset}><DonationBnbLinkContent icon={Icon} isActive={isActive} label={label} /></CustomLink></li>;
    });
    $[6] = menuClickAnimated;
    $[7] = pathname;
    $[8] = requestHaptic;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <ul className="flex flex-1 items-center">{t4}</ul>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t5) {
    t6 = <nav aria-label={"\uAE30\uBD80 \uB0B4\uBE44\uAC8C\uC774\uC158"} className="relative flex size-full items-center p-8">{t3}{t5}</nav>;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
function _temp(event) {
  event.preventDefault();
  event.stopPropagation();
}
function _temp2(state) {
  return state.subDomain === SUB_DOMAIN.TOSS;
}
function _temp3(prev_1) {
  return !prev_1;
}